
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Building2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import PropertyCard from '@/components/ui/PropertyCard';

import { getDeveloperProperties } from '@/services/property';
import { developers } from '@/services/developersData';
import { PropertyType } from '@/types/property';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { pagination } from '@/constants';
import { toast } from '@/hooks/use-toast';
import SEO from '@/components/seo/seo';
import { capitalizeWords, toSentenceCase } from '@/helper';

const DeveloperDetails = () => {
    const { developer } = useParams<{ developer: string }>();
    const [properties, setProperties] = useState<PropertyType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);

    // Find static developer data
    const developerInfo = developers.find(d =>
        d.slug === developer ||
        d.name.toLowerCase() === developer?.toLowerCase() ||
        d.name.toLowerCase().replace(/\s+/g, '-') === developer?.toLowerCase()
    );

    const fetchProperties = async (page = pagination.page) => {
        if (!developer) return;
        setIsLoading(true);
        try {
            const searchName = developer;
            const { data, pagination: paginationData } = await getDeveloperProperties(searchName, {
                page: page,
                limit: pagination.limit
            });

            if (Array.isArray(data)) {
                setProperties(data);
                if (paginationData) {
                    setTotalPages(paginationData.totalPages);
                }
            } else {
                console.error("Unexpected API response format:", data);
            }

        } catch (error) {
            console.error("Error fetching properties:", error);
            toast({
                title: "Error",
                description: "Failed to fetch properties.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProperties();
    }, [developer]);

    useEffect(() => {
        fetchProperties();
    }, [developer]);

    if (isLoading) {
        return (

            <div className="flex-grow pt-32 pb-16">
                <div className="container mx-auto px-4">
                    {/* Back Button Skeleton */}
                    {/* <div className="mb-8">
                            <Skeleton className="h-6 w-32" />
                        </div> */}

                    {/* Developer Header Skeleton */}
                    <div className="bg-card rounded-xl border shadow-sm p-6 md:p-8 mb-12">
                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            <Skeleton className="w-full md:w-48 h-32 rounded-lg shrink-0" />
                            <div className="flex-1 space-y-4 w-full">
                                <div>
                                    <Skeleton className="h-10 w-3/4 md:w-1/2 mb-2" />
                                    <div className="flex items-center gap-2">
                                        <Skeleton className="h-4 w-4" />
                                        <Skeleton className="h-4 w-48" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-2/3" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Properties Grid Skeleton */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-8 w-64" />
                            <Skeleton className="h-5 w-32" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="rounded-3xl overflow-hidden border bg-card">
                                    <Skeleton className="w-full h-[300px]" />
                                    <div className="p-6 md:p-7">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Skeleton className="h-5 w-5 rounded-full" />
                                            <Skeleton className="h-4 w-40" />
                                        </div>
                                        <Skeleton className="h-7 w-full mb-2" />
                                        <Skeleton className="h-7 w-3/4 mb-5" />
                                        <div className="flex items-baseline gap-2 mb-5">
                                            <Skeleton className="h-9 w-40" />
                                        </div>
                                        <div className="flex justify-between border-t pt-5 gap-4">
                                            <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-8" /><Skeleton className="h-3 w-10" /></div></div>
                                            <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-8" /><Skeleton className="h-3 w-10" /></div></div>
                                            <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-12" /><Skeleton className="h-3 w-10" /></div></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <SEO
                title={`${toSentenceCase(developerInfo?.name)} | Urban Oasis UAE`}
                description={developerInfo?.about}
                url={`/developers/${developer}`}
                image="/image/logo.png"
                keywords={`${developer},dubai real estate, urban oasis`}
            />
            <div className="flex-grow pt-32 pb-16">
                <div className="container mx-auto px-4">
                    {/* Back Button */}
                    <Link to="/developers" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Developers
                    </Link>

                    {/* Developer Header */}
                    <div className="bg-card rounded-xl border shadow-sm p-6 md:p-8 mb-12">
                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            {developerInfo?.logo && (
                                <div className="w-full md:w-48 h-32 bg-white rounded-lg border p-4 flex items-center justify-center shrink-0">
                                    <img
                                        src={developerInfo.logo}
                                        alt={developerInfo.name}
                                        className={`max-w-full max-h-full ${developerInfo.logoClass || 'object-contain'}`}
                                    />
                                </div>
                            )}
                            <div className="flex-1 space-y-4">
                                <div>
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                                        {developerInfo?.name || developer}
                                    </h1>
                                    <div className="flex items-center text-muted-foreground">
                                        <Building2 className="h-4 w-4 mr-2" />
                                        <span>Premium Real Estate Developer</span>
                                    </div>
                                </div>
                                {developerInfo?.about && (
                                    <p className="text-muted-foreground leading-relaxed max-w-3xl">
                                        {developerInfo.about}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Properties Grid */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold">Properties by {developerInfo?.name || developer}</h2>
                            <span className="text-muted-foreground">{properties.length} Properties Found</span>
                        </div>

                        {properties.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {properties.map((property) => (
                                    <PropertyCard key={property._id} property={property} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-secondary/20 rounded-xl border border-dashed">
                                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                <h3 className="text-xl font-semibold mb-2">No Properties Found</h3>
                                <p className="text-muted-foreground max-w-md mx-auto">
                                    We couldn't find any active listings for this developer at the moment.
                                    Please check back later or explore other developers.
                                </p>
                                <Button className="mt-6" asChild>
                                    <Link to="/properties">View All Properties</Link>
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="mt-12 flex justify-center">
                            <PaginationControl
                                totalPages={totalPages}
                                onPageChange={fetchProperties}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DeveloperDetails;
